(() => {
  'use strict';
  const script = document.currentScript;
  const preview = script?.dataset.preview === 'true';
  const designMode = Boolean(window.Shopify?.designMode);
  const videoURL = script.dataset.video;
  const spriteURL = 'https://cdn.shopify.com/s/files/1/0886/8351/2151/files/colibri-sprite-vol.webp?v=1789048165';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const duration = 4200;
  const settleDuration = 950;
  let raf = 0;
  let timer = 0;
  let video = null;
  let sprite = null;
  let bird = null;
  let glow = null;
  let context = null;
  let generation = 0;
  let running = false;
  let dirty = true;
  let target = null;
  let startTime = null;
  let lastMediaFrame = -1;
  let playButton = null;

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
  const ease = t => t * t * (3 - 2 * t);
  const reduced = () => motion.matches;

  function geometry() {
    const nodes = document.querySelectorAll('pc-navigation .pcnav-logo-image, .shopify-section-header .header__heading-logo');
    for (const logo of nodes) {
      const r = logo.getBoundingClientRect();
      if (r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < innerHeight) {
        const css = getComputedStyle(logo);
        if (css.visibility === 'hidden' || css.display === 'none' || Number(css.opacity) === 0) continue;
        const size = clamp(r.width * 0.44, 18, 90);
        return {x: r.left + r.width * 0.47, y: r.top + r.height * 0.41, size};
      }
    }
    return null;
  }

  function stop() {
    generation++;
    running = false;
    cancelAnimationFrame(raf);
    clearTimeout(timer);
    raf = 0;
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.remove();
    }
    video = null;
    bird?.remove();
    glow?.remove();
    bird = null;
    glow = null;
    context = null;
  }

  // Remove residual green and support browsers that decode WebM without alpha.
  // The turquoise plumage is retained: only green exceeding BOTH red and blue
  // is keyed out, with a soft transition at the feather edges.
  function removeGreenBackground() {
    const frame = context.getImageData(0, 0, 384, 216);
    const pixels = frame.data;
    for (let i = 0; i < pixels.length; i += 4) {
      if (pixels[i + 3] === 0) continue;
      const red = pixels[i];
      const green = pixels[i + 1];
      const blue = pixels[i + 2];
      const excess = green - Math.max(red, blue);
      if (excess <= 12) continue;
      const coverage = green > 50 ? 1 - clamp((excess - 12) / 38, 0, 1) : 1;
      pixels[i + 3] = Math.round(pixels[i + 3] * coverage);
      pixels[i + 1] = Math.min(green, Math.max(red, blue) + 12);
    }
    context.putImageData(frame, 0, 0);
  }

  function paint(elapsed) {
    if (video && video.readyState >= 2) {
      const mediaFrame = Math.floor(video.currentTime * 24);
      if (mediaFrame === lastMediaFrame) return;
      context.clearRect(0, 0, 384, 216);
      context.drawImage(video, 0, 0, 384, 216);
      removeGreenBackground();
      if (preview && lastMediaFrame === -1) {
        const pixels = context.getImageData(0, 0, 384, 216).data;
        let visiblePixels = 0;
        for (let i = 3; i < pixels.length; i += 4) if (pixels[i] > 128) visiblePixels++;
        bird.dataset.pcVisiblePixels = String(visiblePixels);
      }
      lastMediaFrame = mediaFrame;
      if (video.currentTime >= 7.9) video.pause();
      return;
    }
    if (sprite?.complete && sprite.naturalWidth) {
      const mediaFrame = Math.min(119, Math.floor((1.25 + elapsed / 1000 * 1.60) * 15));
      if (mediaFrame === lastMediaFrame) return;
      context.clearRect(0, 0, 384, 216);
      context.drawImage(sprite, (mediaFrame % 12) * 192, Math.floor(mediaFrame / 12) * 108, 192, 108, 0, 0, 384, 216);
      removeGreenBackground();
      lastMediaFrame = mediaFrame;
    }
  }

  function animate(timestamp) {
    if (!running || !bird || reduced() || document.hidden) { stop(); return; }
    if (startTime === null) startTime = timestamp;
    const elapsed = timestamp - startTime;
    if (elapsed > duration + settleDuration) { stop(); return; }
    if (dirty || elapsed % 120 < 20) {
      target = geometry();
      dirty = false;
    }
    if (!target) { stop(); return; }
    try { paint(elapsed); } catch (error) {
      if (video) fallback(generation);
      else stop();
      return;
    }
    const progress = clamp(elapsed / duration, 0, 1);
    const t = ease(progress);
    const startX = innerWidth < 750 ? 18 : 28;
    const distance = Math.max(0, target.x - startX);
    const approach = 1 - t;
    const x = target.x - distance * approach;
    const arch = Math.sin(t * Math.PI);
    const y = target.y + 50 * approach - 31 * arch + Math.sin(progress * Math.PI * 5) * 1.3 * approach;
    const initialSize = innerWidth < 750 ? 72 : 96;
    const size = initialSize + (target.size - initialSize) * ease(clamp((progress - 0.42) / 0.58, 0, 1));
    const landing = clamp((elapsed - duration) / settleDuration, 0, 1);
    const opacity = ease(clamp(elapsed / 260, 0, 1)) * (1 - ease(clamp((elapsed - duration + 180) / 980, 0, 1)));
    const tilt = Math.sin(t * Math.PI * 1.8) * 3.3 * approach;
    bird.style.setProperty('--pc-colibri-size', `${size}px`);
    bird.style.transform = `translate3d(${x - size / 2}px,${y - size * 0.28125}px,0) rotate(${tilt}deg)`;
    bird.style.opacity = String(opacity);
    bird.dataset.pcColibriState = progress < 1 ? 'flying' : 'landing';
    glow.style.left = `${target.x}px`;
    glow.style.top = `${target.y}px`;
    glow.style.opacity = String(Math.sin(landing * Math.PI) * 0.55);
    glow.style.transform = `translate(-50%,-50%) scale(${0.8 + landing * 0.35})`;
    raf = requestAnimationFrame(animate);
  }

  function begin(token) {
    if (token !== generation || reduced() || document.hidden) return;
    clearTimeout(timer);
    if (!geometry()) { stop(); return; }
    running = true;
    startTime = null;
    lastMediaFrame = -1;
    dirty = true;
    try { if (!preview && !designMode) sessionStorage.setItem('pcColibriVu', '1'); } catch (error) {}
    raf = requestAnimationFrame(animate);
  }

  function fallback(token) {
    if (token !== generation) return;
    cancelAnimationFrame(raf);
    clearTimeout(timer);
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.load();
      video.remove();
      video = null;
    }
    if (!sprite) {
      sprite = new Image();
      sprite.crossOrigin = 'anonymous';
    }
    if (sprite.complete && sprite.naturalWidth) { begin(token); return; }
    sprite.onload = () => begin(token);
    sprite.onerror = () => { if (token === generation) stop(); };
    sprite.src = spriteURL;
    timer = setTimeout(() => { if (token === generation) stop(); }, 7000);
  }

  function launch(force = false) {
    if (reduced() || document.hidden) return;
    if (!force && !preview && !designMode) {
      try { if (sessionStorage.getItem('pcColibriVu')) return; } catch (error) {}
    }
    stop();
    if (!geometry()) return;
    const token = generation;
    bird = document.createElement('div');
    bird.className = 'pc-colibri-quality-flight';
    bird.setAttribute('aria-hidden', 'true');
    bird.dataset.pcColibriQuality = 'hd';
    const canvas = document.createElement('canvas');
    canvas.width = 384;
    canvas.height = 216;
    context = canvas.getContext('2d', {willReadFrequently: true});
    if (!context) { stop(); return; }
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    bird.append(canvas);
    glow = document.createElement('div');
    glow.className = 'pc-colibri-quality-glow';
    glow.setAttribute('aria-hidden', 'true');
    document.body.append(bird, glow);
    video = document.createElement('video');
    video.className = 'pc-colibri-media';
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    video.defaultPlaybackRate = 1.60;
    video.playbackRate = 1.60;
    document.body.append(video);
    if (!video.canPlayType('video/webm')) { fallback(token); return; }
    video.addEventListener('error', () => fallback(token), {once: true});
    video.addEventListener('loadedmetadata', () => {
      if (token !== generation || !video) return;
      video.currentTime = 1.25;
    }, {once: true});
    video.addEventListener('seeked', () => {
      if (token !== generation || !video) return;
      video.play().then(() => begin(token)).catch(() => fallback(token));
    }, {once: true});
    video.src = videoURL;
    timer = setTimeout(() => fallback(token), 6500);
  }

  function invalidate() { dirty = true; }
  function sectionReload() { if (designMode) launch(true); }
  function setup() {
    if (preview && !playButton) {
      playButton = document.createElement('button');
      playButton.type = 'button';
      playButton.className = 'pc-colibri-preview-control';
      playButton.textContent = document.documentElement.lang.startsWith('fr') ? '↻ Rejouer le colibri' : '↻ Replay the bird';
      playButton.addEventListener('click', () => launch(true));
      document.body.append(playButton);
    }
    launch();
  }

  window.addEventListener('resize', invalidate, {passive: true});
  window.addEventListener('scroll', invalidate, {passive: true});
  window.addEventListener('pagehide', stop);
  document.addEventListener('visibilitychange', () => { if (document.hidden) stop(); });
  motion.addEventListener('change', () => { if (reduced()) stop(); });
  document.addEventListener('shopify:section:load', sectionReload);
  if (document.readyState === 'complete') setup();
  else window.addEventListener('load', setup, {once: true});
})();

